/**
 * lazyWithRetry — wraps React.lazy with a single auto-reload recovery for
 * stale-chunk errors that surface as "SyntaxError: Unexpected token '<'"
 * or "ChunkLoadError" after a fresh deploy.
 *
 * What's happening:
 *   - The browser cached an older index.html that references hashed JS
 *     chunks (e.g. `static/js/admin.OLD_HASH.chunk.js`).
 *   - After deploy those chunks no longer exist; the CDN's SPA fallback
 *     serves index.html for the missing JS request.
 *   - The browser tries to parse the HTML as JS and throws.
 *
 * Fix: when import() rejects, set a one-shot sessionStorage flag and
 *      reload the page. The reload fetches the fresh index.html with the
 *      new chunk names and the dynamic import succeeds. The flag prevents
 *      reload loops if the failure is real (e.g. network down).
 */
import { lazy } from "react";

const FLAG = "lodestar:lazy-retry-attempted";

export function lazyWithRetry(factory) {
    return lazy(async () => {
        try {
            const m = await factory();
            // success → clear the flag so future failures can retry once.
            try { window.sessionStorage.removeItem(FLAG); } catch { /* noop */ }
            return m;
        } catch (err) {
            const alreadyRetried = (() => {
                try { return window.sessionStorage.getItem(FLAG) === "1"; }
                catch { return false; }
            })();
            if (!alreadyRetried) {
                try { window.sessionStorage.setItem(FLAG, "1"); } catch { /* noop */ }
                // Force a hard reload — browser refetches index.html.
                window.location.reload();
                // Return a placeholder so React doesn't error before reload completes.
                return { default: () => null };
            }
            // We've already reloaded once; let the error propagate so the
            // Sentry ErrorBoundary catches it instead of looping forever.
            throw err;
        }
    });
}
